import React, { useEffect, useState } from "react";
import $ from "jquery";
import { Edit3, Image as ImageIcon, Save, Upload } from "lucide-react";
import ApiURl from "../../controllers/Api";

const EMPTY = { id: "", icon_key: "image", image_url: "", sort_order: 1, is_active: "1" };

export default function AdminAwards() {
  const [awards, setAwards] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchAwards = async () => {
    try {
      const response = await $.getJSON(`${ApiURl}getAwards.php`);
      setAwards(response?.success ? response.data || [] : []);
    } catch (error) {
      console.error("Error loading awards:", error);
      setMessage("Could not load awards.");
    }
  };

  useEffect(() => {
    fetchAwards();
  }, []);

  const saveAward = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => payload.append(key, value));
    if (imageFile) payload.append("image", imageFile);

    try {
      const response = await $.ajax({
        url: `${ApiURl}saveAward.php`,
        type: "POST",
        data: payload,
        processData: false,
        contentType: false,
        dataType: "json",
      });
      setMessage(response?.message || "Award saved.");
      if (response?.success) {
        setForm(EMPTY);
        setImageFile(null);
        setPreview("");
        fetchAwards();
      }
    } catch (error) {
      console.error("Error saving award:", error);
      setMessage("Server error while saving award.");
    } finally {
      setSaving(false);
    }
  };

  const editAward = (award) => {
    setForm({
      id: award.id || "",
      icon_key: "image",
      image_url: award.image_path || "",
      sort_order: award.sort_order || 1,
      is_active: String(award.is_active ?? 1),
    });
    setImageFile(null);
    setPreview(award.image_url || "");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setForm(EMPTY);
    setImageFile(null);
    setPreview("");
  };

  return (
    <main className="min-h-screen bg-[#F7EFE7] p-4 font-[Kanit] text-[#2B0000] lg:p-7">
      <div className="mx-auto max-w-[1200px]">
        <section className="mb-6 rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-5 shadow-sm">
          <h1 className="font-serif text-4xl">Awards</h1>
          <p className="mt-2 text-sm text-[#6f5e57]">Add, edit and hide/show awards on the public Gallery page.</p>
        </section>

        {message && <div className="mb-5 rounded-xl border border-[#d4af37]/30 bg-[#fff7e6] px-4 py-3 text-sm font-semibold text-[#7d5a19]">{message}</div>}

        <form onSubmit={saveAward} className="mb-7 rounded-[24px] border border-[#d8c6b7] bg-white p-5 shadow-sm">
          <h2 className="mb-4 font-serif text-2xl">{form.id ? "Update Award" : "Add Award"}</h2>
          <div className="grid gap-4 lg:grid-cols-[160px_160px]">
            <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none" />
            <select value={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.value })} className="rounded-xl border border-[#d6c4b5] px-4 py-3 outline-none">
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
          <div className="mt-4 rounded-2xl border border-[#decfc3] bg-[#fffaf5] p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <ImageIcon className="h-4 w-4 text-[#8f6f38]" />
              Award Photo
            </div>
            <div className="mb-4 h-48 overflow-hidden rounded-xl border border-[#d8c6b7] bg-[#fbf3eb]">
              {preview ? (
                <img src={preview} alt="Award preview" className="h-full w-full object-cover" />
              ) : (
                <div className="grid h-full place-items-center text-sm text-[#9b877e]">No image selected</div>
              )}
            </div>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm font-semibold">
              <Upload className="h-4 w-4" />
              Upload Photo
              <input
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setImageFile(file);
                  if (file) setPreview(URL.createObjectURL(file));
                }}
                className="hidden"
              />
            </label>
          </div>
          <div className="mt-5 flex gap-3">
            <button disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-5 py-3 text-sm font-semibold text-[#f4dfae] disabled:opacity-60">
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : form.id ? "Update Award" : "Add Award"}
            </button>
            {form.id && <button type="button" onClick={resetForm} className="rounded-xl border border-[#d6c4b5] px-5 py-3 text-sm font-semibold">Cancel</button>}
          </div>
        </form>

        <section className="overflow-hidden rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] shadow-sm">
          {awards.map((award) => {
            return (
              <div key={award.id} className="flex flex-wrap items-center justify-between gap-4 border-b border-[#eadfd5] p-4 last:border-b-0">
                <div className="flex items-center gap-4">
                  <span className="grid h-14 w-14 overflow-hidden rounded-xl bg-[#fbf3eb] text-xs text-[#9b877e]">
                    {award.image_url ? <img src={award.image_url} alt="Award" className="h-full w-full object-cover" /> : "No image"}
                  </span>
                  <div>
                    <h3 className="font-serif text-xl">Award Image</h3>
                    <p className="text-sm text-[#7d6c64]">Order {award.sort_order} - {award.is_active ? "Active" : "Inactive"}</p>
                  </div>
                </div>
                <button onClick={() => editAward(award)} className="inline-flex items-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold">
                  <Edit3 className="h-4 w-4" />
                  Edit
                </button>
              </div>
            );
          })}
          {!awards.length && <p className="p-6 text-sm text-[#7d6c64]">No awards added yet.</p>}
        </section>
      </div>
    </main>
  );
}

